"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";

import { cn } from "@/lib/utils";
import type { IPageAuthor } from "@/types/author";
import {
  PREVIEW_AUTHOR,
  buildBylineMetaLine,
  isBlogPageType,
  resolveAuthorProfileHref,
  type IAuthorPageMeta,
} from "@/utils/authorWidget";

interface AuthorByline1Props {
  data?: any;
  /** Author linked to the page (server: `page.author`), injected by NewPage. */
  author?: IPageAuthor | null;
  /** Page type, injected by NewPage. Anything but "blog" hides the widget. */
  pageCategory?: string;
  /** updated_at / read_time / views off the page row, injected by NewPage. */
  pageMeta?: IAuthorPageMeta | null;
  isCmsPreview?: boolean;
}

const POPPINS = "var(--font-poppins), Poppins, sans-serif";

/** Exported from the Atlas share cluster (13114:32395); they carry their own colours. */
const SHARE_ICONS = {
  copy: "/svg/share-link.svg",
  whatsapp: "/svg/share-whatsapp.svg",
  linkedin: "/svg/linkedin.png",
};

const isOn = (v: unknown) => v === true || v === 1 || v === "1";

/** Swapped in for the link icon for a beat after a successful copy. */
function CopiedIcon() {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      className="size-[18px]"
    >
      <path
        d="M3.75 9.5 7.25 13l7-7.5"
        stroke="#1DA462"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Author Byline 1 (section 3004 / variant 3000) — Atlas 13114:32412 (desktop) and
 * 13116:455 (mobile). "By <author>" plus the article's meta line, with the share
 * cluster alongside it on desktop.
 *
 * RENDERS ONLY ON A BLOG PAGE WITH AN AUTHOR LINKED. The name comes from that
 * author and the meta line from the page row (updated_at / read_time / views), so
 * the only config is colours and which share links appear.
 *
 * Share targets the live page URL, resolved after mount — reading location during
 * render would not match what the server produced.
 */
export default function AuthorByline1({
  data,
  author: pageAuthor,
  pageCategory,
  pageMeta,
  isCmsPreview,
}: AuthorByline1Props) {
  const row = data?.data ?? data ?? {};

  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") setShareUrl(window.location.href);
  }, []);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    const url =
      shareUrl || (typeof window !== "undefined" ? window.location.href : "");
    if (!url) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // navigator.clipboard is undefined outside a secure context (plain http on
        // a LAN IP, for one), so fall back rather than leaving a dead button.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy the page link:", err);
    }
  }, [shareUrl]);

  // The CMS widget preview has no page behind it, so it falls back to a stand-in
  // author purely so the editor can see the colours they are picking.
  const author: IPageAuthor | null =
    pageAuthor ?? (isCmsPreview ? PREVIEW_AUTHOR : null);

  if (!isCmsPreview && !isBlogPageType(pageCategory)) return null;
  if (!author?.name) return null;

  const backgroundColor = row.background_color || "transparent";
  const bylineColor = row.byline_text_color || "#000000";
  const metaColor = row.meta_text_color || "rgba(0,0,0,0.6)";
  const shareLabelColor = row.share_label_color || "#000000";
  const shareIconBackground = row.share_icon_background_color || "#FFFFFF";
  const showAuthorLink = isOn(row.show_author_link ?? true);

  const showCopyLink = isOn(row.show_copy_link ?? true);
  const showWhatsApp = isOn(row.show_whatsapp ?? true);
  const showLinkedIn = isOn(row.show_linkedin ?? true);
  const showShare =
    isOn(row.show_share ?? true) &&
    (showCopyLink || showWhatsApp || showLinkedIn);

  const metaLine = buildBylineMetaLine(pageMeta);
  const profileHref = resolveAuthorProfileHref(author);

  const encodedUrl = encodeURIComponent(shareUrl);
  const buttonClass =
    "inline-flex cursor-pointer items-center justify-center rounded-full p-[4px] transition-opacity hover:opacity-80";
  const iconClass = "size-[18px] object-contain";

  return (
    <div
      className="flex w-full flex-col gap-[10px] tab:flex-row tab:items-center tab:gap-[24px]"
      style={{ backgroundColor, fontFamily: POPPINS }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0 tab:gap-[4px]">
        <p
          className="w-full break-words text-[12px] font-medium leading-normal tab:text-[14px]"
          style={{ color: bylineColor }}
        >
          <span>By </span>
          {showAuthorLink ? (
            <Link
              href={profileHref}
              className="underline decoration-solid [text-underline-position:from-font]"
              style={{ color: bylineColor }}
            >
              {author.name}
            </Link>
          ) : (
            <span>{author.name}</span>
          )}
        </p>

        {metaLine ? (
          <p
            className="w-full break-words text-[10px] leading-normal tab:text-[12px]"
            style={{ color: metaColor }}
          >
            {metaLine}
          </p>
        ) : null}
      </div>

      {/* Shown at every width. The mobile frame (13116:455) is the byline text
          alone, so mobile stacks the cluster under it rather than beside it. */}
      {showShare ? (
        <div className="flex items-center gap-[20px]">
          <span
            className="whitespace-nowrap text-[12px] font-medium leading-normal"
            style={{ color: shareLabelColor }}
          >
            Share:
          </span>
          <div className="flex items-center gap-[10px]">
            {showCopyLink ? (
              <button
                type="button"
                onClick={handleCopy}
                aria-label={copied ? "Page link copied" : "Copy page link"}
                title={copied ? "Link copied" : "Copy link"}
                className={cn(buttonClass, "relative")}
                style={{ backgroundColor: shareIconBackground }}
              >
                {copied ? (
                  <CopiedIcon />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={SHARE_ICONS.copy} alt="" className={iconClass} />
                )}
                {copied ? (
                  // Visual only — the button's aria-label already announces the
                  // state, so this must not be read out a second time.
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-[4px] bg-black/80 px-2 py-1 text-[10px] font-medium leading-none text-white"
                  >
                    Copied!
                  </span>
                ) : null}
              </button>
            ) : null}

            {showWhatsApp ? (
              <a
                href={`https://wa.me/?text=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on WhatsApp"
                className={cn(buttonClass, !shareUrl && "pointer-events-none")}
                style={{ backgroundColor: shareIconBackground }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={SHARE_ICONS.whatsapp} alt="" className={iconClass} />
              </a>
            ) : null}

            {showLinkedIn ? (
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on LinkedIn"
                className={cn(buttonClass, !shareUrl && "pointer-events-none")}
                style={{ backgroundColor: shareIconBackground }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={SHARE_ICONS.linkedin} alt="" className={iconClass} />
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
